/**
 * THE ARRANGE FORM PAGE'S MOVES (services spec P-4.10 design 9). CLIENT MOVES ONLY.
 *
 * Every function takes a task's `form` block and returns a NEW one; none changes what it was
 * given. Nothing here compiles a form, decides which questions can be on one, or guesses what
 * WhatsApp will show: that is the server's (`POST …/tasks/form-preview`, `tasks/flow.ts`), and
 * the page draws its preview from that answer only.
 *
 * Any edit returns `mode: 'arranged'`, because an edit IS the owner arranging her form.
 * `autoForm` is the one way back ("Split into pages for me"), and it keeps her words and the
 * questions she keeps in the chat.
 *
 * The caps are the server's (`tasks/flow.ts`, `tasks/form-layout.ts`), repeated so the page can
 * say so before the server answers; the server refuses past them either way.
 */
export const MAX_FORM_PAGES = 5;
export const MAX_FORM_QUESTIONS = 10;
export const MAX_FORM_TITLE = 80;
export const MAX_FORM_INTRO = 300;
export const MAX_FORM_BUTTON = 35;

const FORM_WORDS = ['titleEn', 'titleAr', 'introEn', 'introAr'];
const PAGE_WORDS = ['titleEn', 'titleAr', 'nextEn', 'nextAr'];

const copy = value => JSON.parse(JSON.stringify(value));
const pagesOf = form => (Array.isArray(form?.pages) ? form.pages : []);
const inChatOf = form => (Array.isArray(form?.inChat) ? form.inChat : []);

/** A copy of `form`, arranged, with these pages, and `inChat` only when it holds something. */
const arranged = (form, pages, inChat = inChatOf(form)) => {
  const out = { ...copy(form ?? {}), mode: 'arranged', pages: copy(pages) };
  if (inChat.length > 0) out.inChat = [...inChat];
  else delete out.inChat;
  return out;
};

/** Where a question sits on the form: `{ page, at }`, or `null` when it is not on it. */
export const findField = (form, stepId) => {
  const pages = pagesOf(form);
  for (let page = 0; page < pages.length; page += 1) {
    const at = pages[page].fields.findIndex(field => field.step === stepId);
    if (at >= 0) return { page, at };
  }
  return null;
};

/** How many questions are on the form. */
export const questionCount = form =>
  pagesOf(form).reduce((count, page) => count + page.fields.length, 0);

/** ↑ / ↓: within its page. Past an edge it is the same form, unchanged. */
export const moveField = (form, stepId, delta) => {
  const where = findField(form, stepId);
  if (!where) return form;
  const pages = copy(pagesOf(form));
  const { fields } = pages[where.page];
  const to = where.at + delta;
  if (to < 0 || to >= fields.length) return form;
  [fields[where.at], fields[to]] = [fields[to], fields[where.at]];
  return arranged(form, pages);
};

/** "Move to page": to the end of that page. */
export const moveToPage = (form, stepId, pageIndex) => {
  const where = findField(form, stepId);
  const pages = copy(pagesOf(form));
  if (!where || !pages[pageIndex] || where.page === pageIndex) return form;
  const [field] = pages[where.page].fields.splice(where.at, 1);
  pages[pageIndex].fields.push(field);
  return arranged(form, pages);
};

/** "Add a page": an empty one at the end, up to `MAX_FORM_PAGES`. */
export const addPage = form => {
  const pages = copy(pagesOf(form));
  if (pages.length >= MAX_FORM_PAGES) return form;
  pages.push({ fields: [] });
  return arranged(form, pages);
};

/**
 * "Remove this page": its questions join the page before it, or the start of the next page when
 * it is the first, so nothing she put on the form leaves it. The last page cannot be removed.
 */
export const removePage = (form, index) => {
  const pages = copy(pagesOf(form));
  if (pages.length <= 1 || !pages[index]) return form;
  const [removed] = pages.splice(index, 1);
  if (index > 0) pages[index - 1].fields.push(...removed.fields);
  else pages[0].fields.unshift(...removed.fields);
  return arranged(form, pages);
};

/** "Ask in the chat instead". */
export const toChat = (form, stepId) => {
  const pages = pagesOf(form).map(page => ({
    ...page,
    fields: page.fields.filter(field => field.step !== stepId),
  }));
  const inChat = inChatOf(form).includes(stepId)
    ? inChatOf(form)
    : [...inChatOf(form), stepId];
  return arranged(form, pages, inChat);
};

/** "Put in the form": at the end of the last page. */
export const toForm = (form, stepId) => {
  const pages = copy(pagesOf(form));
  if (pages.length === 0) pages.push({ fields: [] });
  if (!findField(form, stepId))
    pages[pages.length - 1].fields.push({ step: stepId });
  return arranged(
    form,
    pages,
    inChatOf(form).filter(id => id !== stepId)
  );
};

/** "Customer can skip this". Off removes the key, so an untouched question reads as before. */
export const setOptional = (form, stepId, optional) => {
  const pages = pagesOf(form).map(page => ({
    ...page,
    fields: page.fields.map(field => {
      if (field.step !== stepId) return field;
      return optional ? { ...field, optional: true } : { step: field.step };
    }),
  }));
  return arranged(form, pages);
};

/** Words, as typed. A blank box removes the key: blank and absent are the same to the server. */
const withWords = (target, patch, keys) => {
  const out = { ...target };
  keys.forEach(key => {
    if (!(key in patch)) return;
    const value = String(patch[key] ?? '');
    if (value.trim() === '') delete out[key];
    else out[key] = value;
  });
  return out;
};

/** The form title and the opening words. */
export const setFormWords = (form, patch) =>
  arranged(withWords(form ?? {}, patch, FORM_WORDS), pagesOf(form));

/** A page's title and its Next words. */
export const setPageWords = (form, index, patch) => {
  const pages = copy(pagesOf(form));
  if (!pages[index]) return form;
  pages[index] = withWords(pages[index], patch, PAGE_WORDS);
  return arranged(form, pages);
};

/** "Split into pages for me": automatic again, with her words and her chat questions kept. */
export const autoForm = form => {
  const out = { mode: 'auto' };
  FORM_WORDS.forEach(key => {
    const value = form?.[key];
    if (typeof value === 'string' && value.trim() !== '') out[key] = value;
  });
  if (inChatOf(form).length > 0) out.inChat = [...inChatOf(form)];
  return out;
};

/** What the page can say before the server answers (the same codes the server uses). */
export const localErrors = form => {
  if (form?.mode !== 'arranged') return [];
  const pages = pagesOf(form);
  const errors = [];
  if (pages.length > MAX_FORM_PAGES) {
    errors.push({
      stepId: '',
      problem: 'form_too_many_pages',
      detail: String(MAX_FORM_PAGES),
    });
  }
  pages.forEach((page, index) => {
    if (page.fields.length === 0) {
      errors.push({
        stepId: '',
        problem: 'form_page_empty',
        detail: String(index + 1),
      });
    }
  });
  if (questionCount(form) > MAX_FORM_QUESTIONS) {
    errors.push({
      stepId: '',
      problem: 'form_too_many_questions',
      detail: String(MAX_FORM_QUESTIONS),
    });
  }
  return errors;
};

/** The page's errors and the server's, each once. */
export const mergeErrors = (...lists) => {
  const seen = new Set();
  return lists.flat().filter(error => {
    const key = `${error.problem}:${error.stepId}:${error.detail ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * The composer's side (P-4.10 design 4): the form without references to steps the task no longer
 * has, and without the pages that left empty. The SAME object when nothing pointed at a missing
 * step, so an untouched task never reads as edited.
 */
export const withoutSteps = (form, stepIds) => {
  if (!form || typeof form !== 'object') return form;
  const keep = new Set(stepIds);
  const pages = pagesOf(form);
  const inChat = inChatOf(form);
  const missing =
    pages.some(page => page.fields.some(field => !keep.has(field.step))) ||
    inChat.some(id => !keep.has(id));
  if (!missing) return form;
  const out = { ...form };
  if (Array.isArray(form.pages)) {
    out.pages = pages.flatMap(page => {
      const fields = page.fields.filter(field => keep.has(field.step));
      return fields.length === 0 && page.fields.length > 0
        ? []
        : [{ ...page, fields }];
    });
  }
  const left = inChat.filter(id => keep.has(id));
  if (left.length > 0) out.inChat = left;
  else delete out.inChat;
  return out;
};

/** A question's words in `lang`, else its name, else its words in the other language. */
export const questionWords = (step, lang = 'en') => {
  const own = String(
    (lang === 'ar' ? step?.questionAr : step?.questionEn) ?? ''
  ).trim();
  const other = String(
    (lang === 'ar' ? step?.questionEn : step?.questionAr) ?? ''
  ).trim();
  return own || String(step?.name ?? '').trim() || other;
};

/**
 * P-4.10 design 4: the questions the saved form carried that this draft of the task no longer
 * has, named in the owner's own words, for "“{question}” was removed from the task, so it is no
 * longer in the form."
 */
export const removedFromForm = (savedDefinition, definition, lang = 'en') => {
  const still = new Set((definition?.steps ?? []).map(step => step.id));
  const saved = new Map(
    (savedDefinition?.steps ?? []).map(step => [step.id, step])
  );
  return pagesOf(savedDefinition?.form)
    .flatMap(page => page.fields.map(field => field.step))
    .filter(id => !still.has(id) && saved.has(id))
    .map(id => ({ stepId: id, question: questionWords(saved.get(id), lang) }));
};
